import type { GeoArea } from '@wehunt/ui'
import { groundShapes } from '@wehunt/ui'

/** Grounds a drive's base area can be drawn from. */
export interface DriveBaseGrounds {
  /** Hunt areas — the club's borders. `areaType: 'hunt'`. */
  boundaryAreas: readonly GeoArea[]
  /** Sub-areas within those borders. `areaType: 'sub'`. */
  subAreaPolygons: readonly GeoArea[]
}

/**
 * The areas a single drive may be based on, beyond the implicit "Entire hunting
 * ground" option the picker always shows first.
 *
 * Returns EMPTY when the feature is off. That is the whole gate: the drives
 * step then receives no areas, so the picker collapses to "Entire hunting
 * ground" on its own and the step behaves exactly as it did before the feature
 * existed. Nothing branches inside the shared component.
 *
 * Two levels of ground can serve as a base, and which one is offered depends on
 * what the team actually has:
 *
 * - *Sub-areas, when the team has any.* This is the finer granularity and the
 *   one the story is about. Every sub-area is offered: a drive's base area is
 *   chosen per drive, so there is no hunt-wide selection to narrow it against.
 *
 * - *Hunt areas (the borders), when the team has no sub-areas at all.* A club
 *   can hold several separate hunting grounds, and a drive belongs to one of
 *   them. Without this, a team that never drew sub-areas got a picker with a
 *   single option and no way to say which ground a drive sits on, even though
 *   the borders express exactly that.
 *
 * A lone hunt area is deliberately NOT offered: with one border, that option
 * covers the same polygon as "Entire hunting ground", so listing it would ask
 * the user to choose between two identical things. The threshold is two.
 *
 * Order follows the grounds, so the picker is stable.
 *
 * The identity of a no-sub-area base is a GROUND, not a stored record: a club
 * can hold several cadastral parcels that are, to a user, one hunting ground,
 * and `drive.area` has to name the ground rather than whichever parcel happened
 * to be picked first. That identity is `area.areaGroup` when the ground has
 * one, else the ground's own Firestore doc id — the same `area.areaGroup ||
 * area.id` key `groundShapes` already buckets its 'subarea' layer by. It is
 * deliberately NOT a geometry-derived id (a prior version minted a rounded
 * centroid): a centroid is this web client's own invention, which the .NET API
 * cannot validate and mobile cannot reproduce, while an `areaGroup`/doc id is a
 * stable identity both platforms already share.
 */
export function resolveDriveBaseAreas(
  enabled: boolean,
  grounds: DriveBaseGrounds,
): GeoArea[] {
  if (!enabled) return []

  const { subAreaPolygons, boundaryAreas } = grounds

  if (subAreaPolygons.length === 0) {
    // Group the same way the map already groups a 'subarea' layer: by
    // `area.areaGroup || area.id`. Several stored parcels sharing one
    // `areaGroup` — an imported property, say — collapse into one base; a
    // border with no group keeps its own doc id. Any member of a bucket can be
    // asked for its own `areaGroup || id`, because that is exactly the value
    // every member of the bucket computed to land there together.
    const areaById = new Map(boundaryAreas.map((a) => [a.id, a] as const))
    const bases = groundShapes(boundaryAreas, 'subarea').flatMap((shape): GeoArea[] => {
      const sample = areaById.get(shape.areaIds[0])
      // No member found means the shape carries no real area to anchor an id
      // to, which `groundShapes` never actually produces — defensive only.
      if (!sample) return []
      return [{
        id: sample.areaGroup || sample.id,
        name: shape.name,
        rings: shape.rings,
        outerCount: shape.outerCount,
        color: shape.color,
      }]
    })
    // A boundary area with no DRAWABLE geometry yields no base here, because
    // `groundShapes` drops any area without a ring long enough to enclose
    // something. That is intentional for this feature, not an oversight: the
    // estate map restriction a drive base feeds is geometry-based on both
    // clients, so a base area must carry a polygon they can restrict to. A base
    // with no geometry cannot express "hunters see only the planned drives" —
    // on mobile it would either fail open or resolve to an empty polygon that
    // hides everything — so a team whose borders carry no coordinates is offered
    // no base area rather than a non-restricting one.
    return bases.length > 1 ? bases : []
  }

  return [...subAreaPolygons]
}
